import { memo, useCallback, useMemo } from 'react';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';
import { isoToDateOnly } from 'utils/formattingUtil';

interface ICreatedAtFilterProps {
  from: string;
  until: string;
  onChange: (from: string, until: string) => void;
}

export const CreatedAtFilter = memo(({ from, until, onChange }: ICreatedAtFilterProps) => {
  const initialValue = useMemo(() => {
    if (!from && !until) {
      return '';
    }
    return JSON.stringify({
      startDate: isoToDateOnly(from),
      endDate: isoToDateOnly(until)
    });
  }, [from, until]);

  const handleApply = useCallback(
    (value: string): void => {
      if (!value) {
        onChange('', '');
        return;
      }
      try {
        const parsed = JSON.parse(value) as { startDate?: string; endDate?: string };
        const fromIso = parsed.startDate ? `${parsed.startDate}T00:00:00.000Z` : '';
        const untilIso = parsed.endDate ? `${parsed.endDate}T23:59:59.999Z` : '';
        onChange(fromIso, untilIso);
      } catch {
        onChange('', '');
      }
    },
    [onChange]
  );

  const selectedItems = useMemo(
    () => (initialValue ? [initialValue] : []),
    [initialValue]
  );

  return (
    <FilterPopover
      label="Created At"
      filterType={FilterType.DATE_PICKER}
      selectedItems={selectedItems}
      onChange={handleApply}
      dataTestId="offers-created-at-filter"
      isLocalTime={false}
    />
  );
});

CreatedAtFilter.displayName = 'CreatedAtFilter';
